import React from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import {areSameConnections} from '../../utils';

import {Screenshot} from './Screenshot';
import {ScreenshotKeyConnectionView} from './ScreenshotKeyConnectionView';

import {ProjectKeysStatuses, ScreenshotKeyConnection} from '@bumpy/i18n_common';

type Props = {
  connections: ScreenshotKeyConnection[];
  screenshotName: string;
  keysStatuses: ProjectKeysStatuses;
  projectUuid: string;
  onSave: (name: string, connections: ScreenshotKeyConnection[]) => void;
  onClose: () => void;
};

export const ProjectScreenshotDialog: React.FC<Props> = (props: Props) => {
  const [connections, setConnections] = React.useState<
    ScreenshotKeyConnection[]
  >(() => JSON.parse(JSON.stringify(props.connections)));
  const [name, setName] = React.useState<string>(props.screenshotName);
  const [lang, setLang] = React.useState<string>('en');
  const [focusedIndex, setFocusedIndex] = React.useState<number | null>(null);
  const activatedColorPicker =
    React.useRef<(color: string, final: boolean) => void>();

  const texts: (string | undefined)[] = [];
  const keysStatusesPerLang = props.keysStatuses[lang];
  for (const connection of connections) {
    const text = keysStatusesPerLang[connection.key];
    if (text) {
      texts.push(text.text);
    } else {
      texts.push(undefined);
    }
  }

  return (
    <Dialog
      open={true}
      onClose={props.onClose}
      sx={{'& .MuiDialog-paper': {width: '100%', maxWidth: '900px'}}}
    >
      <DialogContent>
        <Stack direction="row" justifyContent="space-between">
          <TextField
            autoFocus
            label="Screenshot name"
            margin="none"
            variant="standard"
            value={name}
            style={{width: 270}}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <Select
            value={lang}
            variant="standard"
            onChange={(event) => {
              setLang(event.target.value);
            }}
          >
            {Object.keys(props.keysStatuses).map((lang) => (
              <MenuItem key={lang} value={lang}>
                {lang}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <Stack direction="row" flex={1} marginTop={2}>
          <Screenshot
            connections={connections}
            focusedIndex={focusedIndex}
            projectUuid={props.projectUuid}
            screenshotName={props.screenshotName}
            texts={texts}
            onColorPick={activatedColorPicker.current}
            onConnectionsChange={setConnections}
            onFocusIndex={setFocusedIndex}
          />
          <Stack flex={1} marginLeft={2}>
            {connections.map((connection, index) => (
              <ScreenshotKeyConnectionView
                activateColorPick={(callback: (color: string) => void) => {
                  activatedColorPicker.current = (
                    color: string,
                    final: boolean,
                  ) => {
                    if (final) {
                      activatedColorPicker.current = undefined;
                    }
                    callback(color);
                  };
                }}
                focused={focusedIndex === index}
                key={`connection-${index}`}
                label={`Key ${index + 1}`}
                connection={connection}
                keysStatusesPerLang={props.keysStatuses[lang]}
                selected={connection.key}
                onFocus={() => {
                  setFocusedIndex(index);
                }}
                onBlur={() => {
                  setFocusedIndex(null);
                }}
                onDelete={() => {
                  const newConnections = [...connections];
                  newConnections.splice(index, 1);
                  setConnections(newConnections);
                }}
                onChange={(connection) => {
                  const newConnections = [...connections];
                  newConnections[index] = connection;
                  setConnections(newConnections);
                }}
              />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          disabled={
            name === props.screenshotName &&
            areSameConnections(connections, props.connections)
          }
          onClick={() => {
            props.onSave(name, connections);
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
